import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import LeadContactFormContent from './LeadContactFormContent';
import StatusMessage from './StatusMessage';
import {
  setInitialContent,
  submitContactForm,
  setFieldValue,
  setFieldError,
  clearErrors,
  addFiles,
  removeFile,
  setFileUploadError,
  clearFiles,
  resetForm,
  resetBlock,
  setAgreementsCheckBox,
} from './leadContactFormSlice';

const LeadContactForm = (props) => {
  useEffect(() => {
    props.actions.setInitialContent(props.iContent);

    return () => {
      props.actions.resetBlock();
    };
  }, []);

  const handleFieldChange = (fieldName, value) => {
    props.actions.setFieldValue({fieldName, value});
  };

  const handleFileChange = (files) => {
    const {iContent} = props;
    const {uploadedFiles} = props.leadContactForm;

    const maxFileCount = iContent.maxFileCount?.value || 3;
    const allowedFileTypes = iContent.allowedFileTypes?.value || '';
    const fileCountExceededMessage = iContent.fileCountExceededMessage?.value || 'חרגת ממספר הקבצים המקסימלי';

    // Clear previous error
    props.actions.setFileUploadError(null);

    // Check if adding these files would exceed max count
    if (uploadedFiles.length + files.length > maxFileCount) {
      props.actions.setFileUploadError(fileCountExceededMessage);
      return;
    }

    // Validate file types if specified
    if (allowedFileTypes && allowedFileTypes.trim() !== '') {
      const allowedTypesArray = allowedFileTypes.split(',').map(type => type.trim().toLowerCase());

      for (const file of files) {
        const fileExtension = file.name.split('.').pop().toLowerCase();

        if (!allowedTypesArray.includes(fileExtension)) {
          props.actions.setFileUploadError(`סוג הקובץ ${fileExtension} אינו מורשה. קבצים מותרים: ${allowedFileTypes}`);
          return;
        }
      }
    }

    // All validations passed, add files
    props.actions.addFiles(files);
  };

  const handleFileRemove = (index) => {
    props.actions.removeFile(index);
    props.actions.setFileUploadError(null);
  };

  const validateForm = () => {
    const {formData, uploadedFiles} = props.leadContactForm;
    const {iContent} = props;
    let isValid = true;
    props.actions.clearErrors();

    // Define field configurations
    const fields = [
      {name: 'firstName', show: iContent.firstNameShow?.value, required: iContent.firstNameRequired?.value, label: iContent.firstNamePlaceholder?.value || 'שם פרטי'},
      {name: 'lastName', show: iContent.lastNameShow?.value, required: iContent.lastNameRequired?.value, label: iContent.lastNamePlaceholder?.value || 'שם משפחה'},
      {name: 'identity', show: iContent.idShow?.value, required: iContent.idRequired?.value, label: iContent.idPlaceholder?.value || 'תעודת זהות', pattern: /^\d{9}$/},
      {name: 'payment', show: iContent.paymentDigitsShow?.value, required: iContent.paymentDigitsRequired?.value, label: iContent.paymentDigitsPlaceholder?.value || '4 ספרות אחרונות', pattern: /^\d{4}$/},
      {name: 'phoneNumber', show: iContent.cellPhoneShow?.value, required: iContent.cellPhoneRequired?.value, label: iContent.cellPhonePlaceholder?.value || 'טלפון נייד', pattern: /^05\d{8}$/},
      {name: 'additionalPhone', show: iContent.otherPhoneShow?.value, required: iContent.otherPhoneRequired?.value, label: iContent.otherPhonePlaceholder?.value || 'טלפון אחר', pattern: /^0\d{8,9}$/},
      {name: 'emailAddress', show: iContent.emailShow?.value, required: iContent.emailRequired?.value, label: iContent.emailPlaceholder?.value || 'דואר אלקטרוני', pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/},
      {name: 'companyName', show: iContent.companyNameShow?.value, required: iContent.companyNameRequired?.value, label: iContent.companyNamePlaceholder?.value || 'שם חברה'},
      {name: 'generalField', show: iContent.generalShow?.value, required: iContent.generalRequired?.value, label: iContent.generalPlaceholder?.value || 'כללי'},
      {name: 'subjectIndex', show: iContent.isShowSubject?.value, required: iContent.isSubjectRequired?.value, label: iContent.subjectListLabel?.value || 'נושא הפניה'},
      {name: 'content', show: iContent.contactMessageShow?.value, required: iContent.contactMessageRequired?.value, label: iContent.contactMessagePlaceholder?.value || 'הודעה'},
    ];

    // Validate form fields
    fields.forEach((field) => {
      if (field.show && field.required) {
        const value = formData[field.name];

        // Special validation for subjectIndex (must be > 0)
        if (field.name === 'subjectIndex') {
          if (!value || value === '' || value === '0') {
            props.actions.setFieldError({
              fieldName: field.name,
              error: `${field.label} הוא שדה חובה`,
            });
            isValid = false;
          }
        }
        // Check if field is empty
        else if (!value || value.trim() === '') {
          props.actions.setFieldError({
            fieldName: field.name,
            error: `${field.label} הוא שדה חובה`,
          });
          isValid = false;
        }
        // Check pattern if provided
        else if (field.pattern && !field.pattern.test(value)) {
          let errorMsg = `${field.label} אינו תקין`;

          if (field.name === 'identity') errorMsg = `${field.label} חייבת להכיל 9 ספרות`;
          else if (field.name === 'payment') errorMsg = `${field.label} - יש להזין 4 ספרות`;
          else if (field.name === 'phoneNumber') errorMsg = `${field.label} אינו תקין (05X-XXXXXXXX)`;
          else if (field.name === 'additionalPhone') errorMsg = `${field.label} אינו תקין`;
          else if (field.name === 'emailAddress') errorMsg = `${field.label} אינה תקינה`;

          props.actions.setFieldError({
            fieldName: field.name,
            error: errorMsg,
          });
          isValid = false;
        }
      }
    });

    // Validate files if file upload is enabled
    if (iContent.allowFileUpload?.value) {
      const maxFileCount = iContent.maxFileCount?.value || 3;

      // Check file count
      if (uploadedFiles && uploadedFiles.length > maxFileCount) {
        const errorMessage = iContent.fileCountExceededMessage?.value || 'חרגת ממספר הקבצים המקסימלי';
        props.actions.setFileUploadError(errorMessage);
        isValid = false;
      }
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    await props.actions.submitContactForm(props.leadContactForm.formData);
  };

  const handleNewForm = () => {
    props.actions.resetForm();
  };

  // Show success/failure message if submitted
  if (props.leadContactForm.submitStatus === 'success') {
    return (
      <StatusMessage
        type="success"
        header={props.iContent.successMessageHeader?.value}
        message={props.iContent.successMessage?.value}
        icon={props.iContent.successIcon}
        backgroundColor={props.iContent.backgroundColor?.value}
        context={props.context}
        onClose={handleNewForm}
      />
    );
  }

  if (props.leadContactForm.submitStatus === 'failure') {
    return (
      <StatusMessage
        type="failure"
        header={props.iContent.failureMessageHeader?.value}
        message={props.iContent.failureMessage?.value}
        icon={props.iContent.failureIcon}
        backgroundColor={props.iContent.backgroundColor?.value}
        context={props.context}
        onClose={handleNewForm}
      />
    );
  }

  // Show form
  return (
    <LeadContactFormContent
      iContent={props.iContent}
      formData={props.leadContactForm.formData}
      errors={props.leadContactForm.errors}
      isSubmitting={props.leadContactForm.isSubmitting}
      uploadedFiles={props.leadContactForm.uploadedFiles}
      fileUploadError={props.leadContactForm.fileUploadError}
      onFieldChange={handleFieldChange}
      onFileChange={handleFileChange}
      onFileRemove={handleFileRemove}
      onSubmit={handleSubmit}
    />
  );
};

const mapStateToProps = (state) => {
  return {
    leadContactForm: state.leadContactForm,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators(
      {
        setInitialContent,
        submitContactForm,
        setFieldValue,
        setFieldError,
        clearErrors,
        addFiles,
        removeFile,
        setFileUploadError,
        clearFiles,
        resetForm,
        resetBlock,
        setAgreementsCheckBox,
      },
      dispatch
    ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LeadContactForm);

